import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const fmt  = n => Number(n||0).toLocaleString('en-US',{maximumFractionDigits:0})
const fmtM = n => { const v=Math.abs(Number(n||0)); if(v>=1e6)return(Number(n)/1e6).toFixed(2)+'M'; if(v>=1e3)return(Number(n)/1e3).toFixed(1)+'K'; return fmt(n) }

const PAYMENT_TERMS = ['Immediate','15 Days','30 Days','45 Days','60 Days','90 Days']
const CURRENCIES    = ['SAR','USD','EUR','GBP','AED','EGP']
const CATEGORIES    = ['Materials','Subcontractor','Equipment','Services','Consultancy','Other']

const btnBase = { display:'inline-flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:6, fontSize:12, fontWeight:600, border:'1px solid transparent', cursor:'pointer', fontFamily:'inherit', transition:'all .12s', whiteSpace:'nowrap' }
const btnP = { ...btnBase, background:'#1A7A4A', color:'#fff', borderColor:'#1A7A4A' }
const btnS = { ...btnBase, background:'#fff', color:'#374151', borderColor:'#D1D5DB' }
const btnG = { ...btnBase, background:'transparent', color:'#6B7280', borderColor:'#E5E7EB' }

const EMPTY_VENDOR = {
  legal_name:'', trade_name:'', vat_number:'', category:'Materials',
  payment_terms:'30 Days', currency:'SAR', contact_name:'',
  contact_email:'', contact_phone:'', address:'', notes:'', is_approved: true,
  cr_number:'', bank_name:'', bank_iban:'', bank_swift:'', beneficiary_name:'',
}

function StatusBadge({ approved }) {
  return (
    <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700,
      background: approved ? '#DCFCE7' : '#FEF3C7',
      color:      approved ? '#14532D' : '#92400E',
      border:     `1px solid ${approved ? '#BBF7D0' : '#FDE68A'}`,
    }}>
      {approved ? 'Approved' : 'Pending'}
    </span>
  )
}

const Field = ({ label, k, type='text', required, placeholder, half, form, errors, set }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#6B7280', marginBottom:5 }}>
      {label}{required && <span style={{ color:'#DC2626', marginLeft:2 }}>*</span>}
    </label>
    <input type={type} value={form[k]||''} onChange={e=>set(k,e.target.value)} placeholder={placeholder}
      style={{ width:'100%', padding:'7px 10px', border:`1px solid ${errors[k]?'#DC2626':'#D1D5DB'}`, borderRadius:6, fontSize:12.5, color:'#0F1017', background:'#fff', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
      onFocus={e=>e.target.style.borderColor='#1A7A4A'} onBlur={e=>e.target.style.borderColor=errors[k]?'#DC2626':'#D1D5DB'}/>
    {errors[k] && <p style={{ fontSize:10, color:'#DC2626', marginTop:3 }}>{errors[k]}</p>}
  </div>
)

const Select = ({ label, k, options, half, form, set }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#6B7280', marginBottom:5 }}>{label}</label>
    <select value={form[k]||''} onChange={e=>set(k,e.target.value)}
      style={{ width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:12.5, color:'#0F1017', background:'#fff', outline:'none', fontFamily:'inherit', boxSizing:'border-box', cursor:'pointer' }}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
)

function VendorModal({ vendor, onSave, onClose, canApprove }) {
  const [form, setForm] = useState(vendor || EMPTY_VENDOR)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setErrors(e => ({ ...e, [key]: undefined })) }

  function validate() {
    const e = {}
    if (!form.legal_name.trim())  e.legal_name  = 'Required'
    if (!form.trade_name.trim())  e.trade_name  = 'Required'
    if (!form.vat_number.trim())  e.vat_number  = 'Required'
    return e
  }

  async function handleSave(draft=false) {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    setSaving(true)
    // fix7: only a user with Vendor.Approve decides the approval state. Everybody else never sends it
    // (the database enforces the same rule: new vendors are created pending and is_approved cannot be changed without Vendor.Approve).
    const payload = { ...form }
    if (canApprove) payload.is_approved = !draft
    else delete payload.is_approved
    let err
    if (vendor?.id) {
      const res = await supabase.from('vendors').update(payload).eq('id', vendor.id)
      err = res.error
    } else {
      const res = await supabase.from('vendors').insert(payload).select().single()
      err = res.error
    }
    setSaving(false)
    if (err) { alert(err.message); return }
    onSave()
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.45)', backdropFilter:'blur(3px)' }}/>
      <div style={{ position:'relative', zIndex:1, width:580, maxHeight:'90vh', background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', boxShadow:'0 24px 64px rgba(0,0,0,.15)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <h2 style={{ fontSize:15, fontWeight:800, color:'#0F1017' }}>{vendor?.id ? 'Edit Vendor' : 'New Vendor'}</h2>
            <p style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>Add to the approved vendor register</p>
          </div>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>
          {/* Legal Info */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #F3F4F6' }}>Legal Information</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Field form={form} errors={errors} set={set} label="Legal Name" k="legal_name" required placeholder="Full registered legal name"/>
            <Field form={form} errors={errors} set={set} label="Trade Name" k="trade_name" required half placeholder="Short / commercial name"/>
            <Field form={form} errors={errors} set={set} label="VAT Number" k="vat_number" required half placeholder="e.g. 310012345600003"/>
            <Field form={form} errors={errors} set={set} label="CR Number" k="cr_number" half placeholder="e.g. 4030125248"/>
            <Select form={form} set={set} label="Category" k="category" options={CATEGORIES} half/>
          </div>

          {/* Commercial */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #F3F4F6' }}>Commercial Terms</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Select form={form} set={set} label="Payment Terms" k="payment_terms" options={PAYMENT_TERMS} half/>
            <Select form={form} set={set} label="Currency" k="currency" options={CURRENCIES} half/>
          </div>

          {/* Bank Information */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #F3F4F6' }}>Bank Information <span style={{ textTransform:'none', fontWeight:400, color:'#C4C9D1' }}>(optional — used on printed PO)</span></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Field form={form} errors={errors} set={set} label="Bank Name" k="bank_name" half placeholder="e.g. Saudi National Bank"/>
            <Field form={form} errors={errors} set={set} label="Beneficiary Name" k="beneficiary_name" half placeholder="Defaults to Legal Name if empty"/>
            <Field form={form} errors={errors} set={set} label="IBAN No." k="bank_iban" half placeholder="SA..."/>
            <Field form={form} errors={errors} set={set} label="Swift Code" k="bank_swift" half placeholder="e.g. NCBKSAJE"/>
          </div>

          {/* Contact */}
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#9CA3AF', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #F3F4F6' }}>Contact Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Field form={form} errors={errors} set={set} label="Contact Name" k="contact_name" half placeholder="Primary contact person"/>
            <Field form={form} errors={errors} set={set} label="Phone" k="contact_phone" half type="tel" placeholder="+966 5x xxx xxxx"/>
            <Field form={form} errors={errors} set={set} label="Email" k="contact_email" type="email" placeholder="vendor@example.com"/>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#6B7280', marginBottom:5 }}>Address</label>
              <textarea value={form.address||''} onChange={e=>set('address',e.target.value)} rows={2} placeholder="Full address"
                style={{ width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:12.5, color:'#0F1017', background:'#fff', outline:'none', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor='#1A7A4A'} onBlur={e=>e.target.style.borderColor='#D1D5DB'}/>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#6B7280', marginBottom:5 }}>Notes</label>
            <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)} rows={2} placeholder="Internal notes about this vendor"
              style={{ width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:12.5, color:'#0F1017', background:'#fff', outline:'none', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor='#1A7A4A'} onBlur={e=>e.target.style.borderColor='#D1D5DB'}/>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>
          <button onClick={onClose} style={btnG}>Cancel</button>
          {/* fix7: "Add to Register" (creates an APPROVED vendor) only for Vendor.Approve; a user without it saves new vendors as Pending,
              and "Update Vendor" keeps the current approval state for them. */}
          {(canApprove || !vendor?.id) && (
            <button onClick={() => handleSave(true)} disabled={saving} style={{ ...(canApprove ? btnS : btnP), opacity:saving?.5:1 }}>{saving && !canApprove ? 'Saving…' : 'Save as Pending'}</button>
          )}
          {(canApprove || vendor?.id) && (
            <button onClick={() => handleSave(false)} disabled={saving} style={{ ...btnP, opacity:saving?.5:1 }}>
              {saving ? 'Saving…' : vendor?.id ? 'Update Vendor' : 'Add to Register'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function VendorMaster() {
  const [vendors, setVendors]   = useState([])
  const [loading, setLoading]   = useState(true)
  const [search,  setSearch]    = useState('')
  const [catFilter, setCatFilter] = useState('all')
  const [modal, setModal]       = useState(null)   // null | 'new' | vendor obj
  const [detail, setDetail]     = useState(null)   // vendor for side panel
  const [poStats, setPoStats]   = useState({})     // vendorId -> {count, total}
  const [toast, setToast]       = useState(null)
  // vendors has no project_id/organization_id (confirmed by schema
  // inspection), so the usual useProjectAuth(projectId) hook cannot be
  // used here (it requires a specific project). Vendor permissions are
  // organization-wide instead, checked via the has_global_permission()
  // RPC directly -- same has_permission()-mirroring principle as
  // useProjectAuth, just without a project scope. Server-side
  // enforcement (has_global_permission() inside the vendor triggers)
  // remains authoritative regardless of what this state returns.
  const [vendorPerms, setVendorPerms] = useState({ view: false, create: false, edit: false, del: false, approve: false })
  useEffect(() => {
    (async () => {
      const checks = await Promise.all([
        supabase.rpc('has_global_permission', { p_permission: 'Vendor.View' }),
        supabase.rpc('has_global_permission', { p_permission: 'Vendor.Create' }),
        supabase.rpc('has_global_permission', { p_permission: 'Vendor.Edit' }),
        supabase.rpc('has_global_permission', { p_permission: 'Vendor.Delete' }),
        supabase.rpc('has_global_permission', { p_permission: 'Vendor.Approve' }),
      ])
      setVendorPerms({
        view: checks[0].data === true,
        create: checks[1].data === true,
        edit: checks[2].data === true,
        del: checks[3].data === true,
        approve: checks[4].data === true,
      })
    })()
  }, [])
  const can = (perm) => {
    if (perm === 'Vendor.View') return vendorPerms.view
    if (perm === 'Vendor.Create') return vendorPerms.create
    if (perm === 'Vendor.Edit') return vendorPerms.edit
    if (perm === 'Vendor.Delete') return vendorPerms.del
    if (perm === 'Vendor.Approve') return vendorPerms.approve
    return false
  }

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('vendors').select('*').order('trade_name')
    setVendors(data || [])
    // Load PO stats per vendor
    const { data: pos } = await supabase.from('purchase_orders')
      .select('vendor_id, total_exc_vat').not('vendor_id','is',null)
    const stats = {}
    for (const po of (pos||[])) {
      if (!po.vendor_id) continue
      if (!stats[po.vendor_id]) stats[po.vendor_id] = { count:0, total:0 }
      stats[po.vendor_id].count++
      stats[po.vendor_id].total += parseFloat(po.total_exc_vat)||0
    }
    setPoStats(stats)
    setLoading(false)
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this vendor from the register?')) return
    await supabase.from('vendors').delete().eq('id', id)
    setDetail(null)
    showToast('Vendor removed')
    load()
  }

  async function toggleApproval(vendor) {
    await supabase.from('vendors').update({ is_approved: !vendor.is_approved }).eq('id', vendor.id)
    load()
    if (detail?.id === vendor.id) setDetail(v => ({ ...v, is_approved: !v.is_approved }))
  }

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase()
    const matchQ = !q || v.legal_name?.toLowerCase().includes(q) || v.trade_name?.toLowerCase().includes(q) || v.vat_number?.includes(q)
    const matchC = catFilter === 'all' || v.category === catFilter
    return matchQ && matchC
  })

  const stats = {
    total:    vendors.length,
    approved: vendors.filter(v => v.is_approved).length,
    totalSpend: Object.values(poStats).reduce((s,p) => s+p.total, 0),
    totalPOs:   Object.values(poStats).reduce((s,p) => s+p.count, 0),
  }

  return (
    <div style={{ minHeight:'100vh', background:'#F3F4F6', fontFamily:'inherit' }}>

      {/* ── Page Header ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'18px 24px 14px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#0F1017', letterSpacing:'-.02em' }}>Vendor Master</h1>
          <p style={{ fontSize:11.5, color:'#6B7280', marginTop:3 }}>Approved vendor register — procurement directory</p>
        </div>
        {can('Vendor.Create') && <button onClick={() => setModal('new')} style={btnP}>+ New Vendor</button>}
      </div>

      {/* ── Summary Bar ── */}
      <div style={{ background:'#0F1017', display:'flex', alignItems:'center', gap:24, padding:'10px 24px', flexWrap:'wrap' }}>
        {[
          { label:'Total Vendors',    value: stats.total,                   color:'#fff' },
          { label:'Approved',         value: stats.approved,                color:'#4ADE80' },
          { label:'Pending',          value: stats.total - stats.approved,  color:'#FBBF24' },
          { label:'Total POs Issued', value: stats.totalPOs,                color:'#fff' },
          { label:'Total Spend',      value: 'SAR '+fmtM(stats.totalSpend), color:'#FBBF24' },
        ].reduce((acc,s,i) => {
          if (i>0) acc.push(<div key={'d'+i} style={{ width:1, height:30, background:'rgba(255,255,255,.1)' }}/>)
          acc.push(
            <div key={s.label} style={{ display:'flex', flexDirection:'column', gap:2 }}>
              <span style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,.38)' }}>{s.label}</span>
              <span style={{ fontSize:15, fontWeight:800, color:s.color, fontVariantNumeric:'tabular-nums' }}>{s.value}</span>
            </div>
          )
          return acc
        }, [])}
      </div>

      <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>

        {/* ── Filter Bar ── */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 10px', background:'#fff', border:'1px solid #D1D5DB', borderRadius:6, flex:1, minWidth:200 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#9CA3AF" strokeWidth="1.4"><circle cx="6.5" cy="6.5" r="4.5"/><line x1="9.5" y1="9.5" x2="14" y2="14"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, trade name, VAT number…"
              style={{ border:'none', background:'none', outline:'none', fontSize:12, color:'#0F1017', width:'100%' }}/>
            {search && <button onClick={()=>setSearch('')} style={{ border:'none', background:'none', cursor:'pointer', color:'#9CA3AF', fontSize:12, padding:0 }}>✕</button>}
          </div>
          <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{ padding:'5px 10px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:12, color:'#0F1017', background:'#fff', cursor:'pointer' }}>
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span style={{ fontSize:11, color:'#9CA3AF', marginLeft:'auto' }}>{filtered.length} vendors</span>
        </div>

        {/* ── Table ── */}
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
          {loading ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:64, gap:10, color:'#9CA3AF' }}>
              <div style={{ width:24, height:24, border:'3px solid #E5E7EB', borderTopColor:'#1A7A4A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
              Loading vendors…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'60px 24px', textAlign:'center', color:'#9CA3AF' }}>
              <p style={{ fontSize:32, marginBottom:10 }}>🏭</p>
              <p style={{ fontSize:14, fontWeight:600, color:'#6B7280' }}>{search ? 'No vendors match your search' : 'No vendors yet'}</p>
              {!search && can('Vendor.Create') && <button onClick={()=>setModal('new')} style={{ ...btnP, marginTop:12 }}>+ Add First Vendor</button>}
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                  {['Trade Name','Legal Name','VAT Number','Category','Payment Terms','Status','POs','Total Spend',''].map(h => (
                    <th key={h} style={{ padding:'9px 14px', textAlign: h===''||h==='POs'||h==='Total Spend'?'right':'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((v, idx) => {
                  const ps = poStats[v.id] || { count:0, total:0 }
                  return (
                    <tr key={v.id}
                      style={{ background: idx%2===0?'#fff':'#FAFAFA', borderBottom:'1px solid #F3F4F6', cursor:'pointer' }}
                      onClick={() => setDetail(v)}
                      onMouseEnter={e=>e.currentTarget.style.background='#F3F9F6'}
                      onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?'#fff':'#FAFAFA'}>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontWeight:700, color:'#0F1017' }}>{v.trade_name}</div>
                        {v.contact_name && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:1 }}>{v.contact_name}</div>}
                      </td>
                      <td style={{ padding:'10px 14px', color:'#6B7280', maxWidth:200 }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.legal_name}</div>
                      </td>
                      <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11.5, color:'#6B7280' }}>{v.vat_number||'—'}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, background:'#F3F4F6', color:'#374151', border:'1px solid #E5E7EB' }}>{v.category||'—'}</span>
                      </td>
                      <td style={{ padding:'10px 14px', color:'#6B7280', fontSize:12 }}>{v.payment_terms||'—'}</td>
                      <td style={{ padding:'10px 14px' }}><StatusBadge approved={v.is_approved}/></td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:600, color:'#0F1017' }}>{ps.count}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, color:'#1A7A4A', fontVariantNumeric:'tabular-nums' }}>{ps.total>0?'SAR '+fmtM(ps.total):'—'}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                          {can('Vendor.Edit') && <button onClick={()=>setModal(v)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:4, cursor:'pointer' }}>Edit</button>}
                          {can('Vendor.Delete') && <button onClick={()=>handleDelete(v.id)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:'1px solid #FECACA', background:'#FEF2F2', color:'#DC2626', borderRadius:4, cursor:'pointer' }}>Delete</button>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Detail Panel ── */}
      {detail && (
        <>
          <div onClick={()=>setDetail(null)} style={{ position:'fixed', inset:0, zIndex:249 }}/>
          <div style={{ position:'fixed', top:0, right:0, width:400, height:'100vh', background:'#fff', borderLeft:'1px solid #E5E7EB', zIndex:250, display:'flex', flexDirection:'column', boxShadow:'-4px 0 24px rgba(0,0,0,.08)' }}>
            <div style={{ padding:'16px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:'#0F1017' }}>{detail.trade_name}</div>
                <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{detail.legal_name}</div>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                {can('Vendor.Edit') && <button onClick={()=>setModal(detail)} style={btnS}>Edit</button>}
                <button onClick={()=>setDetail(null)} style={{ width:28, height:28, borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:18 }}>
              {/* Status + approval toggle */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <StatusBadge approved={detail.is_approved}/>
                {can('Vendor.Approve') && (
                  <button onClick={()=>toggleApproval(detail)}
                    style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:6, cursor:'pointer', fontFamily:'inherit',
                      background: detail.is_approved ? '#FEF2F2' : '#DCFCE7',
                      color:      detail.is_approved ? '#DC2626' : '#14532D',
                      border:     `1px solid ${detail.is_approved ? '#FECACA' : '#BBF7D0'}`,
                    }}>
                    {detail.is_approved ? 'Suspend' : 'Approve'}
                  </button>
                )}
              </div>

              {/* PO Stats */}
              {poStats[detail.id] && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
                  <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px' }}>
                    <p style={{ fontSize:9, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4 }}>POs Issued</p>
                    <p style={{ fontSize:20, fontWeight:800, color:'#0F1017' }}>{poStats[detail.id].count}</p>
                  </div>
                  <div style={{ background:'rgba(26,122,74,.06)', border:'1px solid rgba(26,122,74,.15)', borderRadius:8, padding:'10px 12px' }}>
                    <p style={{ fontSize:9, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4 }}>Total Spend</p>
                    <p style={{ fontSize:16, fontWeight:800, color:'#1A7A4A' }}>SAR {fmtM(poStats[detail.id].total)}</p>
                  </div>
                </div>
              )}

              {/* Details */}
              <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#9CA3AF', marginBottom:8 }}>Vendor Details</div>
              {[
                ['VAT Number',     <span style={{ fontFamily:'monospace', fontSize:12 }}>{detail.vat_number||'—'}</span>],
                ['CR Number',      <span style={{ fontFamily:'monospace', fontSize:12 }}>{detail.cr_number||'—'}</span>],
                ['Category',       detail.category||'—'],
                ['Payment Terms',  detail.payment_terms||'—'],
                ['Currency',       detail.currency||'SAR'],
                ['Contact Name',   detail.contact_name||'—'],
                ['Email',          detail.contact_email ? <a href={`mailto:${detail.contact_email}`} style={{ color:'#1A7A4A' }}>{detail.contact_email}</a> : '—'],
                ['Phone',          detail.contact_phone||'—'],
                ['Address',        detail.address||'—'],
              ].map(([label, value], i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'7px 0', fontSize:12.5, borderBottom:'1px solid #F3F4F6' }}>
                  <span style={{ color:'#6B7280', flexShrink:0, marginRight:12 }}>{label}</span>
                  <span style={{ fontWeight:600, color:'#0F1017', textAlign:'right' }}>{value}</span>
                </div>
              ))}

              {(detail.bank_name || detail.bank_iban || detail.bank_swift || detail.beneficiary_name) && (
                <>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#9CA3AF', marginTop:16, marginBottom:8 }}>Bank Information</div>
                  {[
                    ['Bank Name',        detail.bank_name||'—'],
                    ['Beneficiary Name', detail.beneficiary_name||detail.legal_name||'—'],
                    ['IBAN No.',         <span style={{ fontFamily:'monospace', fontSize:11.5 }}>{detail.bank_iban||'—'}</span>],
                    ['Swift Code',       detail.bank_swift||'—'],
                  ].map(([label, value], i) => (
                    <div key={'bank'+i} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'7px 0', fontSize:12.5, borderBottom:'1px solid #F3F4F6' }}>
                      <span style={{ color:'#6B7280', flexShrink:0, marginRight:12 }}>{label}</span>
                      <span style={{ fontWeight:600, color:'#0F1017', textAlign:'right' }}>{value}</span>
                    </div>
                  ))}
                </>
              )}
              {detail.notes && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:6, fontSize:12, color:'#92400E' }}>
                  <span style={{ fontWeight:600 }}>Notes: </span>{detail.notes}
                </div>
              )}

              {/* Actions */}
              {can('Vendor.Delete') && (
                <div style={{ marginTop:16, display:'flex', gap:8 }}>
                  <button onClick={()=>handleDelete(detail.id)}
                    style={{ flex:1, padding:'8px', borderRadius:6, border:'1px solid #FECACA', background:'#FEF2F2', color:'#DC2626', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                    Delete Vendor
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Modal ── */}
      {modal && (
        <VendorModal
          vendor={modal==='new' ? null : modal}
          onSave={() => { setModal(null); setDetail(null); load(); showToast(modal==='new'?'Vendor added':'Vendor updated') }}
          onClose={() => setModal(null)}
          canApprove={can('Vendor.Approve')}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:500, background:'#1A7A4A', color:'#fff', padding:'10px 18px', borderRadius:8, fontSize:13, fontWeight:600, boxShadow:'0 4px 16px rgba(0,0,0,.15)' }}>
          ✓ {toast}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
